import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Observable, Subject, empty, from } from 'rxjs';
import { map, switchMap, reduce } from 'rxjs/operators';

import { ExportToCsv } from 'export-to-csv';
import { saveAs } from 'file-saver';

import { ErrorMessageService, CacheService } from '@library/core/services';
import { RelativeDateService } from '@library/common/datehandler';
import { HttpPaginationService, PaginationOptions } from '@library/modules/http-pagination';

import { ReportDetailsService } from '../report.details.service';

import { Report } from '../report';
import { ReportRequest } from './report-request';
import { ReportResponse } from './report-response';
import { ReportOutput } from './report-output';

@Injectable()
export class ReportService {
   url = 'shipment/report';

   report: Report;
   response: ReportResponse = new ReportResponse();

   refresh$: Subject<void> = new Subject();

   response$: Observable<boolean> = this.refresh$.pipe(
      map( _ => !!this.response.collection ),
   );

   get responseColumns() { return Object.keys( this.response.headers ); }

   constructor(
      private cache: CacheService,
      private error: ErrorMessageService,
      private http: HttpClient,
      private snackbar: MatSnackBar,
      public pagination: HttpPaginationService,
      public rdate: RelativeDateService,

      private reportDetails: ReportDetailsService,
   ) { }

   private normalizeDateToUtcMidnightIso(value: any): any {
      let date: Date;
      let fromUtcString = false;

      if (value instanceof Date) {
         date = value;
      } else if (value?._isAMomentObject || typeof value?.toDate === 'function') { // added to fix moment.js date objects
         date = value.toDate();
      } else if (typeof value === 'string' && value) {
         date = new Date(value);
         if (isNaN(date.getTime())) { return value; }
         fromUtcString = true;
      } else {
         return value;
      }

      const year  = fromUtcString ? date.getUTCFullYear() : date.getFullYear();
      const month = fromUtcString ? date.getUTCMonth()    : date.getMonth();
      const day   = fromUtcString ? date.getUTCDate()     : date.getDate();

      const mm = String(month + 1).padStart(2, '0');
      const dd = String(day).padStart(2, '0');

      return `${year}-${mm}-${dd}`;
   }

   load(): Observable<ReportResponse> {
      this.reset();

      const r = this.reportDetails.report$.getValue();
      if ( !r?.filters?.length || !r?.schema?.length ) {
         this.pagination.stop();
         this.snackbar.open( `Error: Missing or Invalid Report Structure`, '', {
            duration: 5000,
            verticalPosition: 'bottom',
         });

         window.history.back();

         return empty();
      }

      this.report = r;

      const options = new PaginationOptions({
         url: this.url,
         params: new ReportRequest({
            schema: this.report.schema,
            filters: this.report.filters.slice().map( f => ({
               name: f.name,
               value: this.normalizeDateToUtcMidnightIso( !f.relative ? f.value : this.rdate.getDate( f.value ) ),
            })),
            doLineItems: this.report.doLineItems,
         }),
         refresher$: this.refresh$,
         responseCallbacks: {
            headers: h => this.response.headers = h,
            actions: a => this.response.actions = a,
            collection: c => this.response.collection = [ ...( this.response.collection || [] ), ...c ],
         },
         useLambda: true,
      });

      this.pagination.loading = true;

      return this.pagination.paginateHttp( options ).pipe(
         // Suppress warnings and notificiations
         // tap( _ => this.error.messages.getValue().map(( e, i ) => e.type !== 'ERROR' ? this.error.clear( i ) : null )),
         map( _ => this.response ),
      );
   }

   sanitizeData( data: any[] ) {
      return data.map( obj => {
         Object.keys( obj ).map( key => {
            if ( typeof( obj[key] ) === 'string' ) {
               // check for double quotes and re-escape
               obj[key] = obj[key].replace( /\\"|"/g, '\\"' );

               // sanitize results for unicode characters -- why?...
               try {
                  obj[key] = JSON.parse( `"${obj[key]}"` );
               } catch ( error ) {
                  console.log( 'unable to parse', key, '|', obj[key] );
                  obj[key] = '[ERROR]';
                  if ( !this.error.none ) {
                     this.error.push( 'Malformed data present and marked with [ERROR]' );
                  }
               }
            }
         });

         return obj;
      });
   }

   sanitizeHeaders( headers: any ) {
      Object.keys( headers ).map( key =>
         headers[key] = JSON.parse( `"${headers[key]}"` ));

      return headers;
   }

   header( name: string ): string {
      return Object.keys( this.response.headers ).filter( key => key === name )
         .map( k => this.response.headers[k] )[0];
   }

   column_width$( name: string ): Observable<number> {
      return this.reportDetails.columns.pipe(
         map( columns => columns.filter( c => c.name === name )[0] ),
         map( column => 10 + ( column?.width ?? 200 )),
      );
   }

   total_column_width$(): Observable<number> {
      return from( this.responseColumns ).pipe(
         switchMap( column => this.column_width$( column )),
         reduce(( a, c ) => a + c, 0 ),
      );
   }

   download( output: ReportOutput ): void {
      if ( !this.report ) { return; }

      // make the request
      const request = new ReportRequest({
         schema: this.report.schema,
         filters: this.report.filters.slice().map( f => ({
            name: f.name,
            value: this.normalizeDateToUtcMidnightIso( !f.relative ? f.value : this.rdate.getDate( f.value ) ),
         })),
         doLineItems: this.report.doLineItems,
         output,
      });

      this.http.post( this.url, request, { responseType: 'blob' })
         .subscribe( blob => saveAs( blob, output.name + '.pdf' ));
   }

   downloadCsv( data: any[] ) {
      new ExportToCsv({
         showLabels: true,
         filename: this.report.name || 'Report_Preview',
         headers: Object.keys( this.response.headers ).map( key => this.response.headers[key] ),
      }).generateCsv( data );
   }

   downloadJson( data: any[] ) {
      const payload = [];

      data.map( row => {
         const tmp_row = {};
         Object.keys( row ).map( k => {
            let header: string = this.response.headers[ k ].replace(/\s/g, '');

            return tmp_row[ header ] = row[ k ];
         });
         payload.push( tmp_row );
      });

      const name = this.report.name.replace(/\s/g, '_') || 'Report_Preview';

      const blobValue = new Blob([ JSON.stringify( payload )], { type: 'application/json' });
      saveAs( blobValue, name + '.json' );
   }

   reset() {
      this.report = null;
      this.response = new ReportResponse();
      this.cache.clearSet( this.url );
      this.pagination.stop();
      this.refresh$.next();
   }
}
